import i18n from "i18next";
import type { AutoSaveStatus } from "@/hooks/useAutoSave";
import { useTranslation } from "@/utils/hooks/useTranslation";

interface StatusBarProps {
  saveStatus: AutoSaveStatus;
  isDirty: boolean;
  blockCount: number;
  /** Total word count across text-bearing blocks. Optional — when omitted, the chip is hidden. */
  wordCount?: number;
  /** Currently visible section, e.g. `{ number: "2.2", title: "Typological Seriation" }`.
   *  When present, renders as a leading chip in the whisper (LILIA-117). */
  currentSection?: { number?: string; title: string } | null;
  lastSavedAt?: Date | null;
  /** The reader may not edit: say so instead of a save state. */
  readOnly?: boolean;
}

/**
 * Status whisper — Quiet redesign (Pass 3, 2026-04-28; tightened LILIA-114).
 *
 * Replaces the old dark StatusBar that bordered the canvas. Now a single
 * dotted line of metadata in the canvas margin: no surface, no border,
 * no contrast cliff. Tokens scoped via .editor-shell — see editor.scss.
 *
 * Mode label removed 2026-04-29: the mode bar in the top bar is the
 * single source of truth (Pass 3 contract — "no Visual at the bottom").
 * Validation, save state, and block count moved into the TopBar's
 * validation pip; this row is now informational metadata only.
 */
export function StatusBar({ saveStatus, isDirty, blockCount, wordCount, currentSection, lastSavedAt, readOnly = false }: StatusBarProps) {
  const { t } = useTranslation('editor');

  const formatRelative = (date: Date) => {
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return t('common:saved');
    if (mins === 1) return `${t('common:saved')} 1 min ago`;
    if (mins < 60) return `${t('common:saved')} ${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    return `${t('common:saved')} ${hrs}h ago`;
  };

  const statusText =
    readOnly ? i18n.t("editor:statusBar.viewOnly")
    : saveStatus === "offline" ? "Offline — saved on this device"
        : saveStatus === "saving" ? t('savingStatus')
          : saveStatus === "error" ? t('saveFailed')
            : isDirty ? t('unsavedChanges')
              : lastSavedAt ? formatRelative(lastSavedAt)
                : t('common:saved');

  // Bullet separator with the same dotted-line cadence as the design mock.
  const dot = <span aria-hidden="true" className="px-2 opacity-60">·</span>;

  return (
    <div
      className="shrink-0 select-none flex items-center justify-center text-center py-2 px-4 bg-transparent"
      style={{
        fontSize: "var(--editor-status-fontsize, 10.5px)",
        letterSpacing: "var(--editor-status-tracking, 0.04em)",
        color: "var(--editor-status-color, var(--lilia-text-muted))",
      }}
    >
      {currentSection && (
        <>
          <span className="truncate max-w-[40ch]">
            {currentSection.number ? `${i18n.t("editor:statusBar.section", { n: currentSection.number })} / ` : ""}
            {currentSection.title}
          </span>
          {dot}
        </>
      )}
      <span>{t('blockCount', { count: blockCount })}</span>
      {typeof wordCount === "number" && (
        <>
          {dot}
          <span>{i18n.t("editor:statusBar.words", { count: wordCount, n: wordCount.toLocaleString() })}</span>
        </>
      )}
      {dot}
      <span data-testid="status-whisper-save" data-unsaved={isDirty ? "true" : "false"}>{statusText}</span>
    </div>
  );
}
